import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2, MapPin, Mail, Phone } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import { toast } from "sonner";
import api from "../lib/api";
import { useContent } from "../lib/content";
import { FAQ } from "../data/content";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

export default function Contact() {
  const cms = useContent();
  const empty = { nom: "", email: "", telephone: "", sujet: "Question générale", message: "", consentement: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.articles().then((r) => setArticles(r.articles || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const hp = e.target.hp?.value || "";
    if (!f.consentement) return toast.error("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try { const r = await api.contact({ ...f, hp }); setDone(r.reference); toast.success("Message transmis !"); }
    catch (err) { toast.error("Une erreur est survenue."); } finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact & FAQ" path="/contact" description="Contactez Speed Détresse à Amiens. Questions fréquentes sur l'abonnement, le Carnet de Santé et les prestations." />
      <PageHero
        overline="Contact & FAQ"
        title={["Parlons de", "votre logement"]}
        subtitle="Une question, un incident, un projet ? Écrivez-nous : chaque message crée un ticket suivi."
        testid="contact-hero"
      />

      <section className="relative z-10 mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr]">
          <Reveal>
            {done ? (
              <div data-testid="contact-success" className="rounded-3xl border border-gold/25 bg-navy-elevated p-10 text-center">
                <CheckCircle2 className="mx-auto h-14 w-14 text-gold" strokeWidth={1.3} />
                <h3 className="mt-5 font-serif text-3xl text-white">Message transmis</h3>
                <p className="mt-3 text-slate-300">Nous revenons vers vous rapidement.</p>
                <p className="mt-4 inline-block rounded-full border border-gold/40 px-4 py-2 font-mono text-sm text-gold">Réf. {done}</p>
                <button onClick={() => { setDone(null); setF(empty); }} className="mt-8 block w-full rounded-full border border-white/15 px-6 py-3 text-sm text-white hover:border-gold/50">Nouveau message</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-5 rounded-3xl border border-white/10 bg-navy-surface/60 p-8 md:p-10">
                <input type="text" name="hp" defaultValue="" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <input required data-testid="contact-nom" placeholder="Nom" value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} className={inputCls} />
                  <input data-testid="contact-telephone" placeholder="Téléphone" value={f.telephone} onChange={(e) => setF({ ...f, telephone: e.target.value })} className={inputCls} />
                </div>
                <input required type="email" data-testid="contact-email" placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} />
                <select data-testid="contact-sujet" value={f.sujet} onChange={(e) => setF({ ...f, sujet: e.target.value })} className={inputCls}>
                  {["Question générale", "Signaler un incident", "Abonnement", "Prestation", "Autre"].map((s) => <option key={s} className="bg-navy">{s}</option>)}
                </select>
                <textarea required rows="5" data-testid="contact-message" placeholder="Votre message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={inputCls} />
                <label className="flex items-start gap-3 text-xs text-slate-400">
                  <input type="checkbox" data-testid="contact-consent" checked={f.consentement} onChange={(e) => setF({ ...f, consentement: e.target.checked })} className="mt-0.5 accent-gold" />
                  J'accepte que mes données soient traitées pour répondre à ma demande.
                </label>
                <button type="submit" disabled={busy} data-testid="contact-submit" className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-sm font-semibold text-navy transition-colors hover:bg-gold-light disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Envoyer le message <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-4">
              {[
                { icon: MapPin, t: "Zone d'intervention", d: cms("site.zone", "Amiens & secteur environnant") },
                { icon: Mail, t: "E-mail", d: cms("site.email", "speeddetresse@gmail.com") },
                { icon: Phone, t: "Téléphone", d: cms("site.phone", "07 68 57 02 27") },
              ].map((item) => (
                <div key={item.t} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-navy-surface/60 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/40 text-gold"><item.icon className="h-5 w-5" strokeWidth={1.5} /></span>
                  <div><p className="text-sm text-white">{item.t}</p><p className="text-sm text-slate-400">{item.d}</p></div>
                </div>
              ))}
              {articles.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-navy-surface/60 p-6">
                  <Overline>Conseils & actualités</Overline>
                  <ul className="mt-4 space-y-3">
                    {articles.slice(0, 3).map((a) => (
                      <li key={a.slug} className="border-b border-white/5 pb-3 last:border-0">
                        <Link to={`/conseils/${a.slug}`} className="flex items-start justify-between gap-4 hover:opacity-90">
                          <div>
                            <span className="text-xs uppercase tracking-wide text-gold">{a.tag}</span>
                            <p className="text-sm text-slate-200">{a.title}</p>
                          </div>
                          <span className="whitespace-nowrap text-xs text-slate-500">{a.read}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to="/conseils" className="mt-4 inline-block text-sm text-gold hover:underline">Tous les conseils →</Link>
                </div>
              )}
            </div>
          </Reveal>
        </div>

        {/* FAQ */}
        <div className="mt-24" data-testid="faq-section">
          <Reveal><Overline>Questions fréquentes</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-serif text-4xl tracking-tight text-white md:text-5xl">Vous vous demandez peut-être…</h2></Reveal>
          <Reveal delay={0.15} className="mt-10">
            <Accordion type="single" collapsible className="mx-auto max-w-3xl">
              {FAQ.map((item, i) => (
                <AccordionItem key={i} value={`i-${i}`} data-testid={`faq-item-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left font-serif text-xl text-white hover:text-gold hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-400">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
